
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Position} from '../core/Position';
import {FieldModelPosition} from '../core/Position';

/**
 * PositionsGetAllResult struct
 */
class PositionsGetAllResult {
  /**
   * Initialize struct
   * @param {!Array=} positions
   * @constructor
   */
  constructor (positions = []) {
    this.Positions = positions
  }

  /**
   * Copy struct (shallow copy)
   * @this {!PositionsGetAllResult}
   * @param {!PositionsGetAllResult} other Other struct
   * @returns {!PositionsGetAllResult} This struct
   */
  copy (other) {
    if (other.Positions != null) {
      this.Positions = []
      for (let item of other.Positions) {
        if (item != null) {
          let tempItem
          tempItem = Position.fromObject(item)
          this.Positions.push(tempItem)
        } else {
          this.Positions.push(null)
        }
      }
    } else {
      this.Positions = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!PositionsGetAllResult}
   * @returns {!PositionsGetAllResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new PositionsGetAllResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new PositionsGetAllResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!PositionsGetAllResult}
   * @param {!PositionsGetAllResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof PositionsGetAllResult)) {
      throw new TypeError('Instance of PositionsGetAllResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!PositionsGetAllResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Positions: ((this.Positions != null) ? Array.from(this.Positions, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return PositionsGetAllResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!PositionsGetAllResult} other Object value
   * @returns {!PositionsGetAllResult} Created struct
   */
  static fromObject (other) {
    return new PositionsGetAllResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!PositionsGetAllResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return PositionsGetAllResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!PositionsGetAllResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10043
  }
}

export { PositionsGetAllResult };

/**
 * Fast Binary Encoding PositionsGetAllResult field model
 */
class FieldModelPositionsGetAllResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Positions = new fbe.FieldModelVector(new FieldModelPosition(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Positions.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Positions.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelPositionsGetAllResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10043
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Positions.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Positions.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Positions.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!PositionsGetAllResult} fbeValue Default value, defaults is new PositionsGetAllResult()
   * @returns {!PositionsGetAllResult} PositionsGetAllResult value
   */
  get (fbeValue = new PositionsGetAllResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!PositionsGetAllResult} fbeValue PositionsGetAllResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Positions.fbeSize) <= fbeStructSize) {
      this.Positions.get(fbeValue.Positions)
    } else {
      fbeValue.Positions.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Positions.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelPositionsGetAllResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelPositionsGetAllResult}
   * @param {!PositionsGetAllResult} fbeValue PositionsGetAllResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelPositionsGetAllResult}
   * @param {PositionsGetAllResult} fbeValue PositionsGetAllResult value
   */
  setFields (fbeValue) {
    this.Positions.set(fbeValue.Positions)
  }
}

export { FieldModelPositionsGetAllResult };

/**
 * Fast Binary Encoding PositionsGetAllResult model
 */
class PositionsGetAllResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelPositionsGetAllResult(this.buffer, 4)
  }

  /**
   * Get the PositionsGetAllResult model
   * @this {!PositionsGetAllResultModel}
   * @returns {!FieldModelPositionsGetAllResult} model PositionsGetAllResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!PositionsGetAllResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!PositionsGetAllResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return PositionsGetAllResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!PositionsGetAllResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelPositionsGetAllResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!PositionsGetAllResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!PositionsGetAllResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!PositionsGetAllResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!PositionsGetAllResultModel}
   * @param {!PositionsGetAllResult} value PositionsGetAllResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!PositionsGetAllResultModel}
   * @param {!PositionsGetAllResult} value PositionsGetAllResult value, defaults is new PositionsGetAllResult()
   * @return {!object} Deserialized PositionsGetAllResult value and its size
   */
  deserialize (value = new PositionsGetAllResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new PositionsGetAllResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new PositionsGetAllResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!PositionsGetAllResultModel}
   * @param {!number} prev Previous PositionsGetAllResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { PositionsGetAllResultModel };
